import { isApiClientError } from "../api/errors";
import type { DocumentWorkflowFileVersion } from "../entities/document-workflow";
import { scanStatusView } from "./fileLabels";
import type { ShellServices } from "./services";

/** Скачивание запрещено ещё до обращения к серверу. */
export class DownloadBlockedError extends Error {
  readonly name = "DownloadBlockedError";

  constructor(readonly reason: string) {
    super(reason);
  }
}

/** Сервер выдал файл, чей SHA-256 не совпал с хешем версии. */
export class DownloadIntegrityError extends Error {
  readonly name = "DownloadIntegrityError";
}

function saveAs(url: string, filename: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/** Сохранить файл по выданной сервером ссылке. */
export function saveSignedFile(url: string, filename: string): void {
  saveAs(url, filename);
}

/**
 * Сохранить файл, пришедший содержимым, а не ссылкой.
 *
 * Так отдаётся отделённая подпись: она лежит в базе рядом с документом, и
 * подписанной ссылки на объект хранилища у неё нет.
 */
export function saveEncodedFile(
  content: string,
  filename: string,
  mime: string,
): void {
  const binary = atob(content);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  try {
    saveAs(url, filename);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Скачать файл по короткоживущей подписанной ссылке, выданной сервером.
 *
 * Постоянного адреса объекта у интерфейса нет: ссылку выдаёт сервер после
 * проверки прав, и она используется сразу, не попадая ни в состояние, ни в
 * кэш запросов. Файл вне состояния `clean` не запрашивается вовсе.
 */
export async function downloadSignedFile(
  load: () => Promise<{
    url: string;
    sha256?: string | null;
    filename?: string;
  }>,
  file: { scan_status: string; sha256: string | null },
  filename: string,
): Promise<void> {
  const view = scanStatusView(file.scan_status);
  if (!view.releasable) {
    throw new DownloadBlockedError(
      view.blockedReason ?? "Скачивание недоступно.",
    );
  }
  const signed = await load();
  // SHA-256 файла известен заранее: выдача другого содержимого — сбой, который
  // нельзя молча сохранять на диск.
  if (
    file.sha256 !== null &&
    typeof signed.sha256 === "string" &&
    signed.sha256.toLowerCase() !== file.sha256.toLowerCase()
  ) {
    throw new DownloadIntegrityError(
      "SHA-256 выданного файла не совпал с хешем версии.",
    );
  }
  saveAs(signed.url, signed.filename ?? filename);
}

/** Скачать версию файла документа. */
export function downloadVersionFile(
  services: Pick<ShellServices, "documentWorkflow">,
  version: Pick<DocumentWorkflowFileVersion, "id" | "scan_status" | "sha256">,
  filename: string,
): Promise<void> {
  return downloadSignedFile(
    () => services.documentWorkflow.downloadVersion(version.id),
    version,
    filename,
  );
}

/** Объяснение неудачи скачивания своими словами. */
export function downloadErrorMessage(error: unknown): string {
  if (error instanceof DownloadBlockedError) return error.reason;
  if (error instanceof DownloadIntegrityError) {
    return "Контрольная сумма файла не совпала с хешем версии: файл не сохранён. Обновите историю версий и повторите попытку.";
  }
  if (isApiClientError(error)) {
    if (error.code === "document_workflow.quarantined_file") {
      return "Сервер не выдаёт этот файл: он ещё не проверен или находится в карантине.";
    }
    if (error.status === 403)
      return "Недостаточно прав для скачивания этого файла.";
    if (error.status === 404)
      return "Версия не найдена или больше не доступна.";
    if (error.status === 503)
      return "Хранилище временно недоступно. Повторите попытку позже.";
  }

  return "Не удалось получить ссылку на скачивание. Повторите попытку.";
}
